import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import type Board from 'webapp/models-custom/board';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';

export default class BoardsIndexController extends Controller {
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;

	settingsNamePrefix = 'boardsViewSettings';

	@tracked isRecentlyDeletedFilterActive = false;

	get settingsName(): string {
		return `${this.settingsNamePrefix}-${this.accountService.id}`;
	}

	get boards(): Board[] {
		return this.mmBoardsService.normalBoardModelsWithDeleted;
	}

	@tracked _viewId = 'grid';
	get viewId(): string {
		return this._viewId;
	}

	set viewId(value: string) {
		this._viewId = value;
		this.viewSettingsManagerService.save(this.settingsName, { viewId: this.viewId }, true);
	}

	get isGridView(): boolean {
		return this.viewId === 'grid';
	}

	get isTableView(): boolean {
		return this.viewId === 'table' && !this.flowTourOnboardingService.isBannerShown;
	}

	get transferedBoardAccountTemplateNotifications(): ResolutionCenterItem[] {
		return this.appNotificationsService.transferedBoardAccountTemplateNotifications;
	}

	get transferedBoardPublicTemplateNotifications(): ResolutionCenterItem[] {
		return this.appNotificationsService.transferedBoardPublicTemplateNotifications;
	}

	get hasBoards(): boolean {
		return (
			this.transferedBoardAccountTemplateNotifications.length > 0 ||
			this.transferedBoardPublicTemplateNotifications.length > 0 ||
			this.boards.length > 0
		);
	}

	get hideRecommendedTemplates() {
		return (
			this.mmBoardsService.normalBoardModels.length >= 3 || this.isRecentlyDeletedFilterActive
		);
	}

	loadViewSettings(): void {
		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.settingsName, true) || {};
		if (savedValues.viewId) {
			this.viewId = savedValues.viewId;
		}
	}

	@action
	createBoard(tagToAdd?: number): void {
		this.mmBoardsService.openDesigner(tagToAdd);
	}

	@action
	createBoardWizard(tagToAdd?: number): void {
		this.mmBoardsService.openWizard(tagToAdd);
	}

	@action
	newBoardOptions(): void {
		const attachTo = $('#navigation-section a.button').get(0);
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: [
					{ action: 'createBoardWizard', text: 'Use wizard', icon: 'Wizard' },
					{ action: 'createBoard', text: 'Start blank', icon: 'PencilRuler' },
				],
				doAction: (actionName: string) => {
					if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
						this[actionName as 'createBoard' | 'createBoardWizard']();
					}
				},
			});
		}
	}

	@action
	copyBoard(fromBoardId: string): void {
		const boardToCopy = this.boards.find((item) => item.id === String(fromBoardId));

		if (boardToCopy && this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			void this.mmBoardsService.duplicateBoard(boardToCopy, (createdBoard) => {
				this.mmBoardsService.openPreview(createdBoard, 'databoards');
			});
		}
	}

	@action
	openView(viewId: string): void {
		if (viewId === 'flow') {
			this.navigationService.transitionToRoute('boards.tabs');
		} else {
			this.viewId = viewId;
		}
	}

	@action
	updateRecentlyDeletedFilter(isActive: boolean): void {
		this.isRecentlyDeletedFilterActive = isActive;
	}
}
